/**
 * Live chat comment list helpers — optimistic reconcile + dedupe.
 * Mirrors mobile LiveController._handleNewComment behavior.
 */

export function commentMatchesPendingOptimistic(pending, comment, currentUserId = null) {
  if (!pending || pending.sendStatus !== 'sending' || !comment) return false
  if (pending.content !== comment.content) return false
  if (pending.userId && comment.userId && pending.userId === comment.userId) return true
  if (pending.pageId && comment.pageId && pending.pageId === comment.pageId) return true
  return !!(currentUserId && pending.userId === currentUserId && comment.userId === currentUserId)
}

export function dedupeCommentsList(comments, maxMessages, matchesPending = commentMatchesPendingOptimistic) {
  const confirmed = new Map()
  const pending = []

  for (const item of comments) {
    if (item.sendStatus === 'sending' || String(item.id).startsWith('pending_')) {
      pending.push(item)
      continue
    }
    if (!item.id) continue
    confirmed.set(item.id, item)
  }

  const filteredPending = pending.filter((item) => {
    for (const confirmedItem of confirmed.values()) {
      if (matchesPending(item, confirmedItem)) return false
    }
    return true
  })

  const next = [...confirmed.values(), ...filteredPending]
  next.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  return next.slice(-maxMessages)
}

export function mergeIncomingComment(comments, comment, meta = {}, options = {}) {
  const {
    maxMessages = 200,
    currentUserId = null,
    matchesPending = (pending, incoming) =>
      commentMatchesPendingOptimistic(pending, incoming, currentUserId),
    dedupe = dedupeCommentsList,
  } = options

  if (!comment?.id) return comments

  const merged = {
    ...comment,
    isOwner: meta.isOwner ?? comment.isOwner ?? false,
    authorRole: String(meta.role ?? comment.authorRole ?? 'viewer').toLowerCase(),
    sendStatus: 'sent',
  }

  const existingIdx = comments.findIndex((c) => c.id === merged.id)
  if (existingIdx >= 0) {
    const next = [...comments]
    const prev = next[existingIdx]
    if (prev.page && !merged.page) merged.page = prev.page
    if (prev.pageId && !merged.pageId) merged.pageId = prev.pageId
    next[existingIdx] = { ...prev, ...merged }
    return dedupe(next, maxMessages, matchesPending)
  }

  const pendingIdx = comments.findIndex((c) => matchesPending(c, merged))
  if (pendingIdx >= 0) {
    const optimistic = comments[pendingIdx]
    if (optimistic.page && !merged.page) merged.page = optimistic.page
    if (optimistic.pageId && !merged.pageId) merged.pageId = optimistic.pageId
    const next = [...comments]
    next[pendingIdx] = merged
    return dedupe(next, maxMessages, matchesPending)
  }

  return dedupe([...comments.slice(-(maxMessages - 1)), merged], maxMessages, matchesPending)
}
